import { all, call, put } from 'redux-saga/effects';
import { requestForSaga } from 'utils/request/request';
import { requestCustomErrorMessage } from 'utils/request/requestCustomErrorMessage';

import { date, isNonNullable } from '@ipfabric/utils';
import { takeEvery } from 'containers/ErrorBoundary';
import _get from 'lodash/get';
import _pickBy from 'lodash/pickBy';
import { FETCH_NETWORK_REPORT_DATA } from './constants';
import { getCountPropertyName, getValue, mapTables } from './helpers';

export function* fetchDownloaReportSaga(action) {
  const { snapshot, resolve, reject, timezone, dateFormat, dateTimeFormat } =
    action.payload;
  try {
    const tableRequest = (
      url,
      columns,
      reports = null,
      filters = null,
      sort = null,
      pagination = null,
    ) => {
      return {
        url,
        method: 'POST',
        data: _pickBy(
          {
            columns,
            snapshot,
            reports,
            filters,
            sort,
            pagination,
          },
          isNonNullable,
        ),
      };
    };
    const getRequest = (url) => ({ url, method: 'GET' });
    const getFilterByColor = (col, colors = ['10', '20', '30']) => ({
      or: colors.map((c) => ({ [col]: ['color', 'eq', c] })),
    });
    const getTableColors = (settings, groups = [], reports = []) => {
      return _get(settings, 'rows', [])
        .reduce((acc, row) => {
          const tableColors = _get(row, 'columns', []).reduce((accCol, col) => {
            return accCol.concat(
              _get(col, 'widgets', [])
                .filter(
                  (r) =>
                    r.key === 'TableColor' &&
                    groups.some((g) => g.id === _get(r, 'props.group')),
                )
                .reduce((acc3, r) => {
                  const foundGroup = groups.find(
                    (g) => g.id === _get(r, 'props.group'),
                  );
                  if (!foundGroup) {
                    return acc;
                  }
                  const reportsMap = foundGroup.children.reduce(
                    (acc4, child) => {
                      const report = reports.find((r2) => r2.id === child.id);
                      if (!report) {
                        return acc;
                      }
                      const succVal = _get(report, 'result.checks.0', 0);
                      const infoVal = _get(report, 'result.checks.10', 0);
                      const warnVal = _get(report, 'result.checks.20', 0);
                      const critVal = _get(report, 'result.checks.30', 0);
                      if (succVal + infoVal + warnVal + critVal > 0) {
                        acc4.push({
                          reportName: report.name,
                          dscGeneral: _get(report, 'descriptions.general', ''),
                          succVal,
                          succDsc: _get(report, 'descriptions.checks.0', ''),
                          infoVal,
                          infoDsc: _get(report, 'descriptions.checks.10', ''),
                          warnVal,
                          warnDsc: _get(report, 'descriptions.checks.20', ''),
                          critVal,
                          critDsc: _get(report, 'descriptions.checks.30', ''),
                        });
                      }
                      return acc4;
                    },
                    [],
                  );
                  acc3.push({ name: foundGroup.name, reports: reportsMap });
                  return acc3;
                }, []),
            );
          }, []);
          return acc.concat(tableColors);
        }, [])
        .sort((a, b) => {
          if (a.name < b.name) {
            return -1;
          }
          if (a.name > b.name) {
            return 1;
          }
          return 0;
        });
    };

    // NIM-13955: Introducing pagination for calls prevent report not being rendered
    const defaultPagination = { limit: 100, start: 0 };

    const requests = {
      bgpStab: tableRequest(
        '/tables/routing/protocols/bgp/neighbors',
        [
          'hostname',
          'localAs',
          'neiAddress',
          'neiHostname',
          'dstInt',
          'neiAs',
          'state',
          'currStateTime',
        ],
        '/technology/routing/bgp/neighbors',
        getFilterByColor('currStateTime'),
        { order: 'asc', column: 'currStateTime' },
        defaultPagination,
      ),
      channelBalance: tableRequest(
        '/tables/interfaces/port-channel/balance/outbound',
        ['hostname', 'intName', 'ratios', 'members'],
        '/technology/port-channels/outbound-balancing-table',
        getFilterByColor('variance'),
        null,
        defaultPagination,
      ),
      configReg: tableRequest(
        '/tables/inventory/devices',
        ['hostname', 'configReg'],
        '/inventory/devices',
        getFilterByColor('configReg', ['20', '30']),
        null,
        defaultPagination,
      ),
      dashboardSettings: getRequest('/settings/dashboard'),
      duplex: tableRequest(
        '/tables/interfaces/duplex',
        [
          'localHostname',
          'localIntName',
          'localDuplex',
          'remoteHostname',
          'remoteIntName',
          'remoteDuplex',
        ],
        '/technology/interfaces/duplex',
        getFilterByColor('status'),
        null,
        defaultPagination,
      ),
      duplicitIp: tableRequest(
        '/tables/addressing/duplicate-ip',
        ['ip', 'count', 'hostname'],
        '/technology/addressing/managed-duplicate-ip',
        getFilterByColor('count'),
        { order: 'desc', column: 'count' },
        defaultPagination,
      ),
      eigrpStab: tableRequest(
        '/tables/routing/protocols/eigrp/neighbors',
        [
          'hostname',
          'localAsn',
          'neiAddress',
          'intName',
          'neiHostname',
          'neiIntName',
          'neiAsn',
          'currStateTime',
        ],
        '/technology/routing/eigrp/neighbors',
        getFilterByColor('currStateTime'),
        { order: 'asc', column: 'currStateTime' },
        defaultPagination,
      ),
      endOfSupport: tableRequest(
        '/tables/reports/eof/summary',
        ['pid', 'pidCount', 'endSupport'],
        '/inventory/end-of-life-milestones/summary',
        getFilterByColor('endSupport'),
        null,
        defaultPagination,
      ),
      fhrpGwPriority: tableRequest(
        '/tables/fhrp/group-state',
        ['hostname', 'group', 'protocol', 'virtualIp', 'intName', 'priority'],
        '/technology/fhrp/group-state',
        {
          and: [
            { and: [{ priority: ['lte', 100] }] },
            { or: [{ state: ['like', 'act'] }, { state: ['like', 'mast'] }] },
          ],
        },
        null,
        defaultPagination,
      ),
      hostVendorMix: getRequest(`/dashboard/hosts?snapshot=${snapshot}`),
      intErrDisabled: tableRequest(
        '/tables/interfaces/errors/disabled',
        ['hostname', 'intName', 'errorReason'],
        '/technology/interfaces/errdisabled',
        getFilterByColor('errorReason'),
        null,
        defaultPagination,
      ),
      isisStab: tableRequest(
        '/tables/routing/protocols/is-is/neighbors',
        [
          'hostname',
          'neiAddress',
          'intName',
          'neiHostname',
          'neiIntName',
          'currStateTime',
        ],
        '/technology/routing/is-is/neighbors',
        getFilterByColor('currStateTime'),
        { order: 'asc', column: 'currStateTime' },
        defaultPagination,
      ),
      networkVendorMix: getRequest(`/dashboard/vendors?snapshot=${snapshot}`),
      ospfStab: tableRequest(
        '/tables/routing/protocols/ospf/neighbors',
        [
          'hostname',
          'intName',
          'localArea',
          'neiAddress',
          'neiHostname',
          'neiIntName',
          'vrf',
          'currStateTime',
        ],
        '/technology/routing/ospf/neighbors',
        getFilterByColor('currStateTime'),
        { order: 'asc', column: 'currStateTime' },
        defaultPagination,
      ),
      poMembers: tableRequest(
        '/tables/interfaces/port-channel/member-status',
        ['hostname', 'intName', 'members'],
        '/technology/port-channels/member-status-table',
        getFilterByColor('members'),
        null,
        defaultPagination,
      ),
      recentReloads: tableRequest(
        '/tables/inventory/devices',
        ['hostname', 'uptime', 'reload', 'vendor'],
        '/inventory/devices',
        { uptime: ['lte', 2629800] },
        { order: 'asc', column: 'uptime' },
        defaultPagination,
      ),
      reports: getRequest(`/reports?snapshot=${snapshot}`),
      reportsGroups: getRequest('/reports/groups'),
      snapshotInfo: tableRequest(
        '/tables/management/discovery-runs',
        [
          'tsEnd',
          'tsStart',
          'deviceCount',
          'interfaceCount',
          'interfaceActiveCount',
          'interfaceEdgeCount',
          'userCount',
        ],
        null,
        null,
        null,
        defaultPagination,
      ),
      telnetEnabled: tableRequest(
        '/tables/inventory/devices',
        ['hostname'],
        '/inventory/devices',
        { loginType: ['eq', 'telnet'] },
        null,
        defaultPagination,
      ),
      unexpectedReloads: tableRequest(
        '/tables/inventory/devices',
        ['hostname', 'sn', 'uptime', 'reload', 'vendor'],
        '/inventory/devices',
        getFilterByColor('reload'),
        null,
        defaultPagination,
      ),
      unexpStpState: tableRequest(
        '/tables/spanning-tree/ports',
        ['hostname', 'vlanId', 'intName', 'portStatus', 'portId', 'rootId'],
        '/technology/spanning-tree/stp-virtual-ports',
        getFilterByColor('portStatus'),
        null,
        defaultPagination,
      ),
      unidirectNeighbors: tableRequest(
        '/tables/neighbors/unidirectional',
        ['localHost', 'localInt', 'remoteHost', 'remoteIp'],
        '/technology/cdp-lldp/unidirectional-neighbors',
        getFilterByColor('localHost', ['10', '20', '30']),
        null,
        defaultPagination,
      ),
      unmgmtNeighbors: tableRequest(
        '/tables/neighbors/unmanaged',
        ['localHost', 'localInt', 'remoteHost', 'remoteIp'],
        '/technology/cdp-lldp/unmanaged-neighbors',
        getFilterByColor('localHost', ['10', '20', '30']),
        null,
        defaultPagination,
      ),
      unsyncNtp: tableRequest(
        '/tables/management/ntp/summary',
        ['hostname', 'confSources'],
        '/technology/management/ntp/summary',
        { synchronizedSources: ['eq', 0] },
        null,
        defaultPagination,
      ),
    };

    const {
      snapshotInfo,
      hostVendorMix,
      networkVendorMix,
      dashboardSettings,
      reports,
      reportsGroups,
      ...rest
    } = yield all(
      Object.keys(requests).reduce((acc, key) => {
        acc[key] = call(requestForSaga, requests[key]);
        return acc;
      }, {}),
    );

    const snapshotInfoRecord = _get(snapshotInfo, ['data', 0], {});
    // Get raw value when colorized column is defined
    for (const key of Object.keys(snapshotInfoRecord)) {
      snapshotInfoRecord[key] = getValue(snapshotInfoRecord[key]);
    }

    const getCountOfRecords = (data) => data?._meta?.count ?? 0;

    const data = Object.keys(rest).reduce(
      (acc, key) => {
        acc[key] = rest[key].data;
        acc[getCountPropertyName(key)] = getCountOfRecords(rest[key]);
        return acc;
      },
      {
        ...snapshotInfoRecord,
        tsEnd: date.formatUtcTimestampInTimeZone(
          _get(snapshotInfoRecord, 'tsEnd'),
          timezone,
          dateTimeFormat,
        ),
        tsStart: date.formatUtcTimestampInTimeZone(
          _get(snapshotInfoRecord, 'tsStart'),
          timezone,
          dateTimeFormat,
        ),
        dashboardTables: getTableColors(
          dashboardSettings,
          reportsGroups,
          reports,
        ),
      },
    );
    resolve({
      charts: {
        networkVendorMix,
        hostVendorMix,
      },
      data: mapTables(data, timezone, dateFormat, defaultPagination.limit),
    });
    yield put({ type: `${FETCH_NETWORK_REPORT_DATA}_SUCCESS` });
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    reject();
    throw e;
  }
}

export function* defaultSaga() {
  yield takeEvery(FETCH_NETWORK_REPORT_DATA, fetchDownloaReportSaga);
}

// All sagas to be loaded
export default defaultSaga;
